import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useQuestionFormsOrchestrator } from '@/data/context/QuestionFormsOrchestrator';
import { useFormRegistration } from '@/data/hook/question/useFormRegistration';
import { useQuestionExamGradeCtx } from '@/data/context/QuestionExamGradeContext';
import { PointsSection } from '../fields/PointsSection';

import { FeedbackCreateDTO } from '@/models/Feedback';
import { Question } from '@/models/Question';
import {
  DEFAULT_LANGUAGE,
  SupportedLanguage,
  TranslatableDict,
} from '@/models/TranslatableField';
import { adapterFeedbacks } from '@/utils/questions/adapterFeedbacks';
import { normalizeTranslatableField } from '@/utils/translatable-field/normalizeTranslatableField';
import { QuestionContextField } from '../fields/QuestionContextField';
import { QuestionTitleField } from '../fields/QuestionTitleField';
import { QuestionFormContainer } from '../QuestionFormContainer';
import QuestionPreview from '@/components/question/forms/QuestionPreview';
import adapterWritingForm from '@/utils/questions/adapterWritingForm';

export type WritingFormFields = {
  title: TranslatableDict;
  context: TranslatableDict;
  feedbacks: FeedbackCreateDTO[];
  maxGrade?: number;
};

type WritingFormProps = {
  isActive?: boolean;
  question: Question;
  isLoading?: boolean;
  onSubmit: (data: WritingFormFields) => Promise<Question>;
};

export default function WritingForm({
  isActive = true,
  question,
  isLoading,
  onSubmit,
}: WritingFormProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.questionsTab.createQuestionModal.writingForm',
  });

  const { identifier } = question;

  const [activeLanguage, setActiveLanguage] =
    useState<SupportedLanguage>(DEFAULT_LANGUAGE);

  const gradeCtx = useQuestionExamGradeCtx();
  const maxGradeDefault = gradeCtx?.getGradeForQuestion(question)?.maxGrade;

  const defaultValues = useMemo<WritingFormFields>(
    () => ({
      title: normalizeTranslatableField(question?.title),
      context: normalizeTranslatableField(question?.context),
      feedbacks: adapterFeedbacks(question?.feedbacks ?? []),
      maxGrade: maxGradeDefault,
    }),
    [question, maxGradeDefault],
  );

  const { submitForm } = useQuestionFormsOrchestrator();

  const methods = useForm<WritingFormFields>({ defaultValues });
  const { watch, trigger, reset, formState } = methods;

  const { hasChanges, handleReset } = useFormRegistration({
    identifier,
    submit: onSubmit,
    validate: trigger,
    watch,
    formState,
    reset,
    defaultValues,
  });

  const realTimeValues = watch();
  const previewQuestion = adapterWritingForm(realTimeValues);

  return (
    <div className="flex gap-6">
      <QuestionFormContainer
        methods={methods}
        activeLanguage={activeLanguage}
        onLanguageChange={setActiveLanguage}
        onSubmit={e => {
          e.preventDefault();
          submitForm(identifier);
        }}
        isLoading={isLoading}
        hasChanges={hasChanges}
        onCancel={handleReset}
      >
        <div className="flex flex-col gap-4">
          <QuestionTitleField
            activeLanguage={activeLanguage}
            placeholder={t('titlePlaceholder')}
          />

          <QuestionContextField
            activeLanguage={activeLanguage}
            isActive={isActive}
          />
        </div>
        <PointsSection />
      </QuestionFormContainer>

      <div className="flex-1">
        <QuestionPreview question={previewQuestion} />
      </div>
    </div>
  );
}
